import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, Download, Trash2, Edit3, Eye, History, MessageCircle } from "lucide-react";
import StudentFormDialog from "@/components/admin/StudentFormDialog";
import FeeHistoryDialog from "@/components/admin/FeeHistoryDialog";
import ReminderDialog from "@/components/admin/ReminderDialog";

export default function StudentsTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyStudent, setHistoryStudent] = useState(null);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderStudent, setReminderStudent] = useState(null);
  const [reminderCounts, setReminderCounts] = useState({});
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/students", { params: search ? { search } : {} });
      setRows(data);
    } finally {
      setLoading(false);
    }
    try {
      const { data } = await api.get("/reminders/counts");
      setReminderCounts(data || {});
    } catch (_) {}
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this student? This can't be undone.")) return;
    try {
      await api.delete(`/students/${id}`);
      toast.success("Student deleted");
      load();
      onChange?.();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("pg_token");
      const res = await fetch(`${API}/export/students`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "students.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="students-tab">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2"
            strokeWidth={2.5}
          />
          <input
            data-testid="students-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, flat, or ID"
            className="w-full pl-11 pr-4 py-3 border-2 border-[#0A0A0A] rounded-full bg-white font-medium focus:outline-none focus:ring-2 focus:ring-[#0A0A0A] focus:ring-offset-2"
          />
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleExport}
            data-testid="students-export-button"
            className="inline-flex items-center gap-2 font-bold px-5 py-3 border-2 border-[#0A0A0A] rounded-full bg-white shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
          >
            <Download size={16} /> Export CSV
          </button>
          <button
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            data-testid="students-add-button"
            className="inline-flex items-center gap-2 font-bold px-5 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
          >
            <Plus size={16} /> Add Student
          </button>
        </div>
      </div>

      <div className="brutal-card p-0 overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="students-table">
            <thead className="bg-[#0A0A0A] text-white">
              <tr>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">ID</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">Name</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">Flat / Room</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">Contact</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">Fees Paid</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">Discount</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">Dues</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">Last Change</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center font-medium text-[#4A4A4A]">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan="9" className="px-4 py-10 text-center font-medium text-[#4A4A4A]">
                    No students yet. Add the first one.
                  </td>
                </tr>
              )}
              {rows.map((s) => {
                const paid = s.dues <= 0;
                return (
                  <tr
                    key={s.id}
                    className="border-t-2 border-[#0A0A0A] hover:bg-[#FFFCE0]"
                    data-testid={`student-row-${s.id}`}
                  >
                    <td className="px-4 py-3 font-mono text-sm font-bold">{s.student_id_number}</td>
                    <td className="px-4 py-3 font-bold">{s.full_name}</td>
                    <td className="px-4 py-3">{s.flat_number}</td>
                    <td className="px-4 py-3">{s.contact_number}</td>
                    <td className="px-4 py-3 font-bold">₹{Number(s.fees_paid).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      {Number(s.discount_amount || 0) > 0 ? (
                        <span
                          className="inline-block px-2.5 py-1 border-2 border-[#0A0A0A] rounded-full text-xs font-bold bg-[#FFE600]"
                          data-testid={`student-discount-${s.id}`}
                        >
                          ₹{Number(s.discount_amount).toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-xs text-[#4A4A4A]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-3 py-1 border-2 border-[#0A0A0A] rounded-full text-xs font-bold ${
                          paid ? "bg-[#00CC66] text-white" : "bg-[#FF4D4D] text-white"
                        }`}
                      >
                        {paid ? "PAID" : `₹${Number(s.dues).toLocaleString()}`}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs font-medium text-[#4A4A4A]" data-testid={`student-lastchange-${s.id}`}>
                      {s.last_fee_change_at
                        ? new Date(s.last_fee_change_at).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => {
                            setReminderStudent(s);
                            setReminderOpen(true);
                          }}
                          data-testid={`student-remind-${s.id}`}
                          className="relative inline-flex items-center gap-1 px-2.5 py-2 border-2 border-[#0A0A0A] rounded-full bg-[#00CC66] text-white text-xs font-bold hover:shadow-brutal-sm transition-shadow"
                          title="Send WhatsApp reminder"
                        >
                          <MessageCircle size={13} strokeWidth={2.5} />
                          {reminderCounts[s.id]?.count > 0 && (
                            <span
                              className="min-w-[1.1rem] h-[1.1rem] inline-flex items-center justify-center rounded-full bg-white text-[#0A0A0A] text-[10px] font-black"
                              data-testid={`student-remind-count-${s.id}`}
                            >
                              {reminderCounts[s.id].count}
                            </span>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setHistoryStudent(s);
                            setHistoryOpen(true);
                          }}
                          data-testid={`student-history-${s.id}`}
                          className="p-2 border-2 border-[#0A0A0A] rounded-full bg-white hover:shadow-brutal-sm transition-shadow"
                          title="Fee history"
                        >
                          <History size={14} strokeWidth={2.5} />
                        </button>
                        <button
                          onClick={() => nav(`/admin/students/${s.id}`)}
                          data-testid={`student-view-${s.id}`}
                          className="p-2 border-2 border-[#0A0A0A] rounded-full bg-[#00E5FF] hover:shadow-brutal-sm transition-shadow"
                          title="View"
                        >
                          <Eye size={14} strokeWidth={2.5} />
                        </button>
                        <button
                          onClick={() => {
                            setEditing(s);
                            setDialogOpen(true);
                          }}
                          data-testid={`student-edit-${s.id}`}
                          className="p-2 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] hover:shadow-brutal-sm transition-shadow"
                          title="Edit"
                        >
                          <Edit3 size={14} strokeWidth={2.5} />
                        </button>
                        <button
                          onClick={() => handleDelete(s.id)}
                          data-testid={`student-delete-${s.id}`}
                          className="p-2 border-2 border-[#0A0A0A] rounded-full bg-[#FF4D4D] text-white hover:shadow-brutal-sm transition-shadow"
                          title="Delete"
                        >
                          <Trash2 size={14} strokeWidth={2.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <StudentFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        student={editing}
        onSaved={() => {
          load();
          onChange?.();
        }}
      />
      <FeeHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        student={historyStudent}
      />
      <ReminderDialog
        open={reminderOpen}
        onOpenChange={setReminderOpen}
        student={reminderStudent}
        onSent={load}
      />
    </div>
  );
}
